/**
 * sessionExpiry — the app's one answer to "your session ended".
 *
 * THE BUG THIS CLOSES (production debug export, 2026-08-26). A session that had
 * been open 3h52m expired at 17:06. Nothing noticed. Every 60-second poller kept
 * firing into the dead session for the next eight minutes — 100 API calls, all
 * 401 "Authentication required", 8 of them surfacing as unhandled promise
 * rejections — while the page sat there looking perfectly healthy and silently
 * refusing to update. The owner only found out because he was asked to log in
 * again somewhere else.
 *
 * `apiClient`'s interceptor already normalises 429 (retry) and 403 (the one
 * permission-denied toast). 401 fell straight through to `Promise.reject`, and
 * nothing anywhere in the frontend handled it.
 *
 * WHY A REDIRECT RATHER THAN A TOAST. A toast leaves every poller running
 * against a session that can never answer, and leaves the user on a page whose
 * Save buttons are all live and all doomed — the screen would keep implying
 * work is being kept. Reloading to the sign-in page stops the pollers dead,
 * re-runs the real auth check, and puts the explanation where the fix is.
 *
 * WHY A `sessionStorage` FLAG rather than a prop. `App.tsx` renders
 * `<LoginPage />` with no props and is a frozen file; the flag lets the login
 * page explain itself without an unlock, and it is per-tab, so a second tab
 * that is still fine is unaffected.
 */

export const SESSION_EXPIRED_KEY = 'bsg.session-expired';

/** Set only by the interceptor; read + cleared once by the login page. */
export function markSessionExpired(): void {
  try { sessionStorage.setItem(SESSION_EXPIRED_KEY, '1'); } catch { /* private mode */ }
}

/** True once, then forgotten — so a later ordinary sign-in is not mislabelled. */
export function consumeSessionExpired(): boolean {
  try {
    const hit = sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1';
    if (hit) sessionStorage.removeItem(SESSION_EXPIRED_KEY);
    return hit;
  } catch {
    return false;
  }
}

/**
 * Client-facing surfaces that have their OWN session and their own sign-in.
 * `/portal` is the signed-in client portal; `/d/` is the tokened share page,
 * which has no session at all.
 */
const CLIENT_SURFACE_PREFIXES = ['/portal', '/d/'];

/**
 * Is the browser currently sitting on a client-facing surface?
 *
 * Read at call time (not module load) because a single-page app changes
 * `pathname` without reloading this module.
 */
function onClientSurface(): boolean {
  try {
    const path = window.location?.pathname || '';
    return CLIENT_SURFACE_PREFIXES.some((p) => path === p || path.startsWith(p));
  } catch {
    return false;
  }
}

/**
 * Should this failure bounce the user to sign-in?
 *
 * Extracted from the interceptor so the redirect-loop guard is testable — it
 * is the one piece here with real teeth. A 401 from an `/auth/` route is the
 * NORMAL answer to "is anyone signed in?", so reacting to it would send the
 * sign-in page bouncing off itself forever.
 *
 * DocNation P5 · THE PORTAL GUARD. Never bounce a client. `/` is the STAFF
 * sign-in — a portal user sent there is not asked to sign in again, they are
 * thrown out of their own app onto a login screen they have no account for,
 * and whatever they were mid-way through writing is gone with the page. The
 * portal mounts shared surfaces that can still reach a staff-gated endpoint by
 * accident (the rich-text merge-field fetch was the first one found), so this
 * closes the whole CLASS rather than the one leak: on `/portal` or `/d/` a 401
 * stays what it is — one failed request, surfaced by whoever made it. The
 * portal's own expiry is the portal's own concern, handled by its auth
 * provider on its own routes.
 */
export function shouldBounceToSignIn(status: number | undefined, url: string | undefined): boolean {
  if (status !== 401) return false;
  if (String(url || '').includes('/auth/')) return false;
  if (onClientSurface()) return false;
  return !sessionExpiryAlreadyHandled();
}

/** Has this tab already bounced? Survives the reload, so it cannot loop. */
export function sessionExpiryAlreadyHandled(): boolean {
  try { return sessionStorage.getItem(SESSION_EXPIRED_KEY) === '1'; } catch { return false; }
}
